import React, { useEffect, useState, useCallback } from "react";
import {
  fetchGateways,
  fetchGatewayConnections,
  fetchConnectionUsers,
  type GatewaySummary,
  type GatewayConnection,
  type GatewayConnectionUser,
} from "../api/client";
import DetailModal from "../components/DetailModal";
import ErrorBanner from "../components/ErrorBanner";
import RegionBadge from "../components/RegionBadge";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";

function accessColor(right: string): string {
  const r = right.toLowerCase();
  if (r.includes("owner") || r.includes("admin")) return "text-red-700 font-semibold";
  if (r.includes("readwrite") || r.includes("write")) return "text-orange-600 font-semibold";
  return "text-gray-700";
}

function TypeBadge({ type }: { type: string }) {
  const t = type.toLowerCase();
  const color = t.includes("sql") ? "bg-blue-50 text-blue-700 border-blue-200"
    : t.includes("oracle") ? "bg-yellow-50 text-yellow-700 border-yellow-200"
    : t.includes("file") || t.includes("folder") ? "bg-purple-50 text-purple-700 border-purple-200"
    : t.includes("web") ? "bg-green-50 text-green-700 border-green-200"
    : "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${color}`}>
      {type || "Unknown"}
    </span>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
      {label}
    </div>
  );
}

// --- Connection detail modal ---

function ConnectionDetailModal({
  connection,
  onClose,
}: {
  connection: GatewayConnection;
  onClose: () => void;
}) {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [users, setUsers] = useState<GatewayConnectionUser[] | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "users" | "reports">("details");

  useEffect(() => {
    if (activeTab === "users" && users === null) {
      setUsersLoading(true);
      fetchConnectionUsers(connection.gatewayId, connection.datasourceId)
        .then(setUsers)
        .catch(() => setUsers([]))
        .finally(() => setUsersLoading(false));
    }
  }, [activeTab, connection.gatewayId, connection.datasourceId, users]);

  const connEntries = Object.entries(connection.connectionDetails || {}).filter(([, v]) => v);
  const label = connection.datasourceName || connEntries.find(([k]) => k === "server" || k === "path" || k === "url")?.[1] || connection.datasourceType;

  return (
    <DetailModal
      title={label}
      subtitle={`${connection.datasourceType}${connection.credentialType ? " · " + connection.credentialType : ""}`}
      onClose={onClose}
    >
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4 -mx-6 px-6">
        {(["details", "users", "reports"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
              activeTab === tab
                ? "border-blue-700 text-blue-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab === "users"
              ? `Users (${connection.userCount})`
              : tab === "reports"
              ? `Reports (${connection.reports.length})`
              : "Details"}
          </button>
        ))}
      </div>

      {/* Details tab */}
      {activeTab === "details" && (
        <div className="space-y-4">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Datasource ID</dt>
              <dd className="mt-0.5 text-sm text-gray-900 font-mono break-all">{connection.datasourceId}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Credential Type</dt>
              <dd className="mt-0.5 text-sm text-gray-900">{connection.credentialType || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Synced</dt>
              <dd className="mt-0.5 text-sm text-gray-900">{formatDate(connection.lastSyncedAt)}</dd>
            </div>
          </dl>
          {connEntries.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mt-4 mb-2">Connection Details</h3>
              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <table className="min-w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {connEntries.map(([k, v]) => (
                      <tr key={k} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-500 w-1/3 align-top">{k}</td>
                        <td className="px-4 py-2 text-gray-800 break-all">{String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Users tab */}
      {activeTab === "users" && (
        usersLoading ? (
          <Spinner label="Loading users…" />
        ) : users && users.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                <th className="pb-2 pr-4">User / Identity</th>
                <th className="pb-2 pr-4">Display Name</th>
                <th className="pb-2 pr-4">Access Right</th>
                <th className="pb-2">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="py-1.5 pr-4 text-gray-700 font-mono text-xs break-all">{u.emailOrId}</td>
                  <td className="py-1.5 pr-4 text-gray-700">{u.displayName || "—"}</td>
                  <td className={`py-1.5 pr-4 ${accessColor(u.datasourceAccessRight)}`}>{u.datasourceAccessRight || "—"}</td>
                  <td className="py-1.5 text-gray-500 text-xs">{u.principalType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-gray-400 py-4">No users found for this connection.</p>
        )
      )}

      {/* Reports tab */}
      {activeTab === "reports" && (
        connection.reports.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                <th className="pb-2 pr-4">Report</th>
                <th className="pb-2">Workspace</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {connection.reports.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="py-1.5 pr-4">
                    {r.webUrl ? (
                      <a href={r.webUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        {r.reportName || r.reportId}
                      </a>
                    ) : (
                      <span className="text-gray-700">{r.reportName || r.reportId}</span>
                    )}
                  </td>
                  <td className="py-1.5 text-gray-500">{r.workspaceName || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-gray-400 py-4">No reports found linked to this connection.</p>
        )
      )}
    </DetailModal>
  );
}

// --- Gateway connections panel ---

function GatewayConnectionsPanel({
  gateway,
  onClose,
}: {
  gateway: GatewaySummary;
  onClose: () => void;
}) {
  const [connections, setConnections] = useState<GatewayConnection[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<GatewayConnection | null>(null);
  const [typeFilter, setTypeFilter] = useState("ALL");

  useEffect(() => {
    fetchGatewayConnections(gateway.gatewayId)
      .then(setConnections)
      .catch((e) => setError(e.message ?? "Failed to load connections"))
      .finally(() => setLoading(false));
  }, [gateway.gatewayId]);

  const types = connections
    ? ["ALL", ...Array.from(new Set(connections.map((c) => c.datasourceType).filter(Boolean))).sort()]
    : ["ALL"];

  const filtered = connections
    ? connections.filter((c) => typeFilter === "ALL" || c.datasourceType === typeFilter)
    : [];

  return (
    <>
      <DetailModal
        title={`Connections — ${gateway.name}`}
        subtitle={`${gateway.type}${gateway.clusterUrl ? " · " + gateway.clusterUrl : ""}`}
        onClose={onClose}
      >
        {loading ? (
          <Spinner label="Loading connections…" />
        ) : error ? (
          <div className="text-sm text-red-600 py-4">{error}</div>
        ) : connections && connections.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">No connections found for this gateway.</p>
        ) : (
          <div className="space-y-3">
            {/* Type filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500">Type:</span>
              {types.map((t) => (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    typeFilter === t
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className="text-xs text-gray-400">{filtered.length} connection(s)</div>

            <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
              {filtered.map((conn) => {
                const connEntries = Object.entries(conn.connectionDetails || {}).filter(([, v]) => v);
                const serverLabel = connEntries.find(([k]) => ["server", "path", "url", "database"].includes(k))?.[1]
                  || conn.datasourceName;
                return (
                  <div
                    key={conn.datasourceId}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelected(conn)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <TypeBadge type={conn.datasourceType} />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 font-medium truncate">
                          {serverLabel || conn.datasourceId}
                        </p>
                        {conn.credentialType && (
                          <p className="text-xs text-gray-400">{conn.credentialType}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                      <span className="text-xs text-gray-500">{conn.userCount} user{conn.userCount !== 1 ? "s" : ""}</span>
                      {conn.reports.length > 0 && (
                        <span className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full">
                          {conn.reports.length} report{conn.reports.length !== 1 ? "s" : ""}
                        </span>
                      )}
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DetailModal>

      {selected && (
        <ConnectionDetailModal connection={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

// --- Main page ---

export default function GatewaysPage() {
  const [gateways, setGateways] = useState<GatewaySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGateway, setSelectedGateway] = useState<GatewaySummary | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchGateways()
      .then(setGateways)
      .catch((e) => setError(e.message ?? "Failed to load gateways"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalConnections = gateways.reduce((s, g) => s + g.connectionCount, 0);
  const totalDatasources = gateways.reduce((s, g) => s + (g.datasourceCount ?? 0), 0);
  const derivedCount = gateways.filter((g) => g.source === "derived").length;
  const unsupportedCount = gateways.filter((g) => (g.unsupportedMembers ?? 0) > 0).length;
  const crossRegionCount = gateways.filter((g) => (g.regionsServed?.length ?? 0) > 1).length;
  const regionsCovered = Array.from(
    new Set(gateways.flatMap((g) => g.regionsServed ?? []))
  ).sort();

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gateways</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            On-premises and VNet data gateway clusters — connections and user permissions.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
        >
          {loading ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : null}
          Refresh
        </button>
      </div>

      {/* Summary chips */}
      {!loading && gateways.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-xs px-2.5 py-1 rounded-full bg-surface-hover text-content-muted font-medium">
            {gateways.length} gateway{gateways.length !== 1 ? "s" : ""}
          </span>
          <span className="text-xs px-2.5 py-1 rounded-full bg-accent-subtle text-accent font-medium">
            {totalDatasources} datasource{totalDatasources !== 1 ? "s" : ""} bound
          </span>
          <span className="text-xs px-2.5 py-1 rounded-full bg-surface-hover text-content-muted font-medium">
            {totalConnections} connection{totalConnections !== 1 ? "s" : ""}
          </span>
          {regionsCovered.length > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-surface-hover text-content-muted font-medium">
              {regionsCovered.join(", ")}
            </span>
          )}
          {crossRegionCount > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-warning-subtle text-warning-fg font-medium">
              {crossRegionCount} cross-region
            </span>
          )}
          {unsupportedCount > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-warning-subtle text-warning-fg font-medium">
              {unsupportedCount} on unsupported builds
            </span>
          )}
        </div>
      )}

      {/* Derived-inventory notice. Without this the counts look like a
          complete picture when connection and permission detail is missing. */}
      {!loading && !error && derivedCount > 0 && (
        <ErrorBanner
          tone="warning"
          message={
            `${derivedCount} of ${gateways.length} gateway${gateways.length !== 1 ? "s" : ""} ` +
            "were identified from datasource bindings rather than the gateway API, so " +
            "their name, cluster URL, connections and user permissions are unavailable. " +
            "The gateway APIs need the delegated Gateway.Read.All and Dataset.Read.All " +
            "permissions, plus gateway-cluster admin rights for the collector account."
          }
        />
      )}

      {loading && <Spinner label="Loading gateways…" />}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={load} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && gateways.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-2">
          <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
          </svg>
          <p className="text-sm font-medium">No gateways found</p>
          <p className="text-xs text-center max-w-sm">
            Gateways are identified both from the Power BI gateway API and from the
            datasource bindings collected every 30 minutes. An empty list here means no
            collected datasource binds to a gateway, so the estate is likely
            cloud-only.
          </p>
        </div>
      )}

      {!loading && !error && gateways.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {gateways.map((gw) => (
            <div
              key={gw.gatewayId}
              onClick={() => gw.connectionCount > 0 && setSelectedGateway(gw)}
              className={`bg-white border border-gray-200 rounded-2xl p-5 shadow-sm transition-shadow ${
                gw.connectionCount > 0 ? "hover:shadow-md cursor-pointer" : ""
              }`}
            >
              {/* Gateway header */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <p className="text-base font-semibold text-content truncate">{gw.name || gw.gatewayId}</p>
                  <p className="text-xs text-content-subtle font-mono truncate mt-0.5" title={gw.gatewayId}>
                    {gw.gatewayId}
                  </p>
                  <p className="text-xs text-content-muted mt-0.5">
                    {[
                      gw.isVirtualNetwork ? "Virtual network" : gw.type,
                      gw.memberCount ? `${gw.memberCount} node${gw.memberCount !== 1 ? "s" : ""}` : "",
                      gw.department,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </p>
                  {/* Regions of the data this gateway moves. Two entries means
                      it carries data across a geographic boundary. */}
                  {gw.regionsServed?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {gw.regionsServed.map((r) => (
                        <RegionBadge key={r} region={r} source="capacity" />
                      ))}
                      {gw.regionsServed.length > 1 && (
                        <span
                          className="inline-block rounded-full bg-warning-subtle px-2 py-0.5 text-xs font-medium text-warning-fg"
                          title="This gateway serves workspaces in more than one geography, so it moves data across a regional boundary"
                        >
                          Cross-region
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {gw.source === "derived" ? (
                  <span
                    className="flex-shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold bg-warning-subtle text-warning-fg"
                    title="Identified from datasource bindings — the gateway API did not return this cluster, so connections and permissions are unavailable"
                  >
                    Inferred
                  </span>
                ) : (
                  <span className="flex-shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold bg-accent-subtle text-accent">
                    {gw.connectionCount} conn.
                  </span>
                )}
              </div>

              {/* What depends on this gateway — the monitoring signal that is
                  available whether or not the gateway API answers. */}
              {/* Two different measurements that can legitimately disagree:
                  "In use" counts bindings from datasets we collect, so it is 0
                  for a gateway no monitored dataset touches; "Configured"
                  counts every connection defined on the cluster itself. */}
              <dl className="grid grid-cols-3 gap-2 mb-3">
                <div className="rounded-lg bg-surface-sunken px-3 py-2">
                  <dt className="text-xs text-content-muted" title="Datasource bindings from the datasets we collect">
                    In use
                  </dt>
                  <dd className="text-lg font-semibold text-content tabular-nums">
                    {gw.datasourceCount ?? 0}
                  </dd>
                </div>
                <div className="rounded-lg bg-surface-sunken px-3 py-2">
                  <dt className="text-xs text-content-muted">Datasets</dt>
                  <dd className="text-lg font-semibold text-content tabular-nums">
                    {gw.datasetCount ?? 0}
                  </dd>
                </div>
                <div className="rounded-lg bg-surface-sunken px-3 py-2">
                  <dt
                    className="text-xs text-content-muted"
                    title="Connections defined on the gateway cluster, whether or not a collected dataset uses them"
                  >
                    Configured
                  </dt>
                  <dd className="text-lg font-semibold text-content tabular-nums">
                    {gw.connectionCount ?? 0}
                  </dd>
                </div>
              </dl>

              {/* Cluster URL */}
              {gw.clusterUrl && (
                <p className="text-xs text-content-subtle font-mono truncate mb-3" title={gw.clusterUrl}>
                  {gw.clusterUrl}
                </p>
              )}

              {/* Member nodes: an agent left on an unsupported build is a
                  control gap, and it is only visible per node. */}
              {gw.members?.length > 0 && (
                <ul className="mb-3 space-y-1">
                  {gw.members.map((m) => (
                    <li key={m.id} className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate text-content-muted" title={m.machine || m.name}>
                        {m.machine || m.name || m.id.slice(0, 8)}
                      </span>
                      <span className="flex flex-shrink-0 items-center gap-1.5">
                        <span className="font-mono text-content-subtle">{m.version || "—"}</span>
                        {m.versionStatus && m.versionStatus !== "Supported" && (
                          <span
                            className="rounded-full bg-warning-subtle px-1.5 py-0.5 font-medium text-warning-fg"
                            title={`Gateway agent build is ${m.versionStatus}`}
                          >
                            {m.versionStatus === "NotSupported" ? "Unsupported" : m.versionStatus}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 pt-3">
                <span>{gw.version ? `v${gw.version}` : "—"}</span>
                {gw.connectionCount > 0 ? (
                  <span className="text-blue-600 font-medium flex items-center gap-1">
                    View connections
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                ) : (
                  <span>No connections</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedGateway && (
        <GatewayConnectionsPanel
          gateway={selectedGateway}
          onClose={() => setSelectedGateway(null)}
        />
      )}
    </div>
  );
}
